// umlaut-check: skip — "zuruecksetzen" ist der technische Name aus
// useGemerkteFilter.jsx, bleibt ASCII.

import React, {useCallback, useEffect, useState} from "react";
import {Container, Spinner} from "react-bootstrap";
import axios from "axios";
import {usePage} from "@inertiajs/react";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import SelectList from "../../Components/SelectList.jsx";
import SzenarioSchalter from "../../Components/Cosmos/SzenarioSchalter.jsx";
import LieferfaehigkeitKacheln from "../../Components/Cosmos/LieferfaehigkeitKacheln.jsx";
import CosmosDatenstandLeiste from "../../Components/Cosmos/CosmosDatenstandLeiste.jsx";
import CosmosPufferKasten from "../../Components/Cosmos/CosmosPufferKasten.jsx";
import LieferfaehigkeitTabelle from "../../Components/Cosmos/LieferfaehigkeitTabelle.jsx";
import useAnalysisData from "../../Components/SalesAnalysis/useAnalysisData.js";
import useGemerkteFilter, {AlleFilterZuruecksetzenLink, useGemerkteTabellenScroll} from "../../Components/Cosmos/useGemerkteFilter.jsx";
import {hasAbility} from "../../Components/SalesAnalysis/format.js";

const PRODUKTLINIE_OPTIONEN = [
    {value: "", label: "Alle Produktlinien"},
    {value: "PREMIUM", label: "PREMIUM"},
    {value: "FLAME_ORANGE", label: "FLAME Orange"},
    {value: "FLAME_BLUE", label: "FLAME Blue"},
    {value: "BURNER", label: "BURNER"},
];

// T2: "In erster Linie interessieren uns hier immer die Premium-Spraydosen" -
// Standard-Produktlinie dieser Seite ist PREMIUM statt "alle".
const STANDARD_FILTER = {
    produktlinie: "PREMIUM",
    szenario: "erwartet",
    nur_kritisch: false,
    suche: "",
    aktive_kachel: "",
    sort_spalte: "",
    sort_richtung: "asc",
};

/** Bildet dieselben vier "gefährdet"-Begriffe wie LieferfaehigkeitQuery::kacheln() clientseitig nach (T4). */
const KACHEL_PRUEFUNG = {
    laeuft_leer: (zeile) => zeile.status === "gerechnet" && zeile.reichweite_tage?.wert !== null && zeile.reichweite_tage?.wert !== undefined && zeile.reichweite_tage.wert <= 28,
    ohne_zusage: (zeile) => zeile.status === "gerechnet" && !zeile.hat_zusage_im_horizont,
    offene_menge_gefaehrdet: (zeile) => zeile.status === "gerechnet" && Boolean(zeile.gefaehrdet),
    zu_spaet: (zeile) => Boolean(zeile.warnung_zu_spaet?.wert),
};

/**
 * Einstiegsseite des Bereichs "Cosmos" (PLAN.md Abschnitt 5,
 * AP3): Kacheln, Datenstand mit "Neu berechnen", Filterleiste und die
 * Farbton-Tabelle mit Kalenderwochen als Spalten. Filterleiste bleibt beim
 * Nachladen (Filterwechsel, "Neu berechnen") sichtbar - angezeigt wird
 * immer der zuletzt erfolgreich geladene Stand, ein kleiner Indikator
 * markiert nur, dass gerade neu geladen wird (kein Verschwinden/Aufblitzen
 * der Filterleiste, Hausregel). Filter, Szenario und Sortierung merkt sich
 * die Seite je Benutzer im Local Storage (T12).
 */
export default function CosmosLieferfaehigkeit() {
    const benutzerId = usePage().props.auth?.id;
    const {addToast} = useExternalControls();
    const {werte, setWert, zuruecksetzen, weichtAb} = useGemerkteFilter("lieferfaehigkeit", STANDARD_FILTER, {benutzerId});
    const {produktlinie, szenario, nur_kritisch: nurKritisch, suche, aktive_kachel: aktiveKachel, sort_spalte: sortSpalte, sort_richtung: sortRichtung} = werte;

    const [ladeSchluessel, setLadeSchluessel] = useState(0);
    const [angezeigt, setAngezeigt] = useState(null);

    const {data, loading, error} = useAnalysisData("/cosmos/daten/lieferfaehigkeit", {
        produktlinie: produktlinie || undefined,
        szenario,
        nur_kritisch: nurKritisch ? 1 : undefined,
        suche: suche || undefined,
        _r: ladeSchluessel || undefined,
    });

    useEffect(() => {
        if (data) {
            setAngezeigt(data);
        }
    }, [data]);

    const neuLaden = useCallback(() => setLadeSchluessel((n) => n + 1), []);

    // T2 Rücksprung vom Artikel-Blatt: Scroll-Position der Tabelle je Seite merken.
    const tabellenRef = useGemerkteTabellenScroll("lieferfaehigkeit", Boolean(angezeigt));

    const kachelKlick = (schluessel) => setWert("aktive_kachel", aktiveKachel === schluessel ? "" : schluessel);

    const sortWechseln = (spalte) => {
        if (sortSpalte !== spalte) {
            setWert("sort_spalte", spalte);
            setWert("sort_richtung", "asc");
        } else {
            setWert("sort_richtung", sortRichtung === "asc" ? "desc" : "asc");
        }
    };

    const wichtigkeitUmschalten = () => setWert("sort_spalte", sortSpalte === "wichtigkeit" ? "" : "wichtigkeit");

    // T8: Stern-Schalter "Top-Farbton" - optimistisch im angezeigten Stand
    // umschalten, bei Fehlschlag zurückrollen. Recht cosmos.parameters greift
    // bereits an der Sichtbarkeit des Schalters (LieferfaehigkeitTabelle.jsx).
    const toggleTop = useCallback((artikelNummer, next) => {
        setAngezeigt((vorher) => {
            if (!vorher) {
                return vorher;
            }
            return {...vorher, zeilen: vorher.zeilen.map((z) => (z.artikel_nummer === artikelNummer ? {...z, top_farbton: next} : z))};
        });

        axios
            .put(`/cosmos/daten/artikel/${artikelNummer}/top`, {top: next})
            .then(() => {
                addToast(<FlexToast variant="success" title="Top-Farbton" message={next ? "Als Top-Farbton markiert." : "Markierung entfernt."} />);
            })
            .catch((e) => {
                setAngezeigt((vorher) => {
                    if (!vorher) {
                        return vorher;
                    }
                    return {...vorher, zeilen: vorher.zeilen.map((z) => (z.artikel_nummer === artikelNummer ? {...z, top_farbton: !next} : z))};
                });
                addToast(
                    <FlexToast
                        variant="danger"
                        title="Top-Farbton"
                        autohide={false}
                        message={e.response?.data?.message || "Speichern fehlgeschlagen."}
                    />
                );
            });
    }, [addToast]);

    const zeilenGefiltert = angezeigt
        ? aktiveKachel && KACHEL_PRUEFUNG[aktiveKachel]
            ? angezeigt.zeilen.filter(KACHEL_PRUEFUNG[aktiveKachel])
            : angezeigt.zeilen
        : [];

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="lieferfaehigkeit">
                Zeigt je Farbton in Farbnummern-Reihenfolge den erwarteten Bestand über die kommenden
                Kalenderwochen, eingefärbt nach Reichweite und Ausfallwoche. Kacheln oben fassen zusammen, wie
                viele Farbtöne bald leer laufen, wie viele ohne Zusage sind und wie viel „offen, nicht reserviert“ (Planung)
                daran hängt. Der Cosmos-Pufferbestand steht getrennt daneben, mit eigenem Datumsstempel.
            </PurposeBox>
            <div className="d-flex align-items-center gap-2 mb-2">
                <h1 className="h4 mb-0">
                    <HeaderWithHelp help="Einstiegsseite des Bereichs Cosmos: die Lieferfähigkeit je Farbton auf einen Blick.">
                        Lieferfähigkeit
                    </HeaderWithHelp>
                </h1>
                {loading && angezeigt && <Spinner animation="border" size="sm" role="status" aria-label="Wird aktualisiert" />}
            </div>

            {error && <p className="text-danger">{error}</p>}

            <div className="d-flex flex-wrap align-items-end gap-3 mb-1">
                <div>
                    <HeaderWithHelp help="Schränkt die Tabelle auf eine der vier Produktlinien ein.">
                        <span className="small text-muted d-block mb-1">Produktlinie</span>
                    </HeaderWithHelp>
                    <SelectList
                        value={produktlinie}
                        options={PRODUKTLINIE_OPTIONEN}
                        onChange={(wert) => setWert("produktlinie", wert)}
                        size="sm"
                        ariaLabel="Produktlinie"
                        style={{minWidth: 190}}
                    />
                </div>

                <SzenarioSchalter value={szenario} onChange={(wert) => setWert("szenario", wert)} hilfeTexte={angezeigt?.szenario_hilfe} />

                <div>
                    <HeaderWithHelp help="Sucht in Farbnummer, Farbname und Artikelnummer.">
                        <span className="small text-muted d-block mb-1">Suche</span>
                    </HeaderWithHelp>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{minWidth: 220}}
                        placeholder="Farbnummer, Name oder Artikelnr."
                        value={suche}
                        onChange={(e) => setWert("suche", e.target.value)}
                    />
                </div>

                <div className="form-check form-switch mb-1">
                    <input
                        id="cosmos-nur-kritisch"
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={nurKritisch}
                        onChange={(e) => setWert("nur_kritisch", e.target.checked)}
                    />
                    <HeaderWithHelp help="Blendet alle Farbtöne aus, die weder bald leer laufen, noch ohne Zusage sind, noch eine Warnung 'zu spät' tragen.">
                        <label htmlFor="cosmos-nur-kritisch" className="form-check-label small">
                            Nur gefährdete
                        </label>
                    </HeaderWithHelp>
                </div>

                <div className="form-check form-switch mb-1">
                    <input
                        id="cosmos-wichtigste"
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={sortSpalte === "wichtigkeit"}
                        onChange={wichtigkeitUmschalten}
                    />
                    <HeaderWithHelp help="Sortiert jede Produktlinien-Gruppe so, dass von Hand markierte Top-Farbtöne zuerst kommen, danach nach Verkaufsmenge der letzten 52 Wochen.">
                        <label htmlFor="cosmos-wichtigste" className="form-check-label small">
                            Wichtigste Farbtöne zuerst
                        </label>
                    </HeaderWithHelp>
                </div>

                <AlleFilterZuruecksetzenLink sichtbar={weichtAb} onClick={zuruecksetzen} />
            </div>

            {angezeigt && (
                <>
                    <CosmosDatenstandLeiste
                        datenstand={angezeigt.datenstand}
                        canRecalculate={hasAbility(angezeigt.meta, "canRecalculate")}
                        onFertig={neuLaden}
                    />

                    <LieferfaehigkeitKacheln kacheln={angezeigt.kacheln} aktiveKachel={aktiveKachel} onKlick={kachelKlick} />

                    <CosmosPufferKasten puffer={angezeigt.puffer} />

                    <LieferfaehigkeitTabelle
                        zeilen={zeilenGefiltert}
                        horizontWochen={angezeigt.horizont_wochen}
                        canArtikel={hasAbility(angezeigt.meta, "canArtikel")}
                        canParameters={hasAbility(angezeigt.meta, "canParameters")}
                        onToggleTop={toggleTop}
                        sortSpalte={sortSpalte}
                        sortRichtung={sortRichtung}
                        onSortWechseln={sortWechseln}
                        tabellenRef={tabellenRef}
                    />
                </>
            )}

            {!angezeigt && loading && (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            )}
        </Container>
    );
}
